import 'hls.js/dist/hls.min';
import '@piczel/ovenplayer';

import { useLocation } from 'react-router';
import React, {
  useEffect, useRef, useState, useMemo, useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { getSourcesForOvenPlayer } from '../StreamPlayer/playerSources';
import { loadStreamScreenshot, streamScreenshotURL } from '../Stream/Helpers';
import {
  AUDIO, HLS, LL_HLS, OVENPLAYER, STREAMSHOTS,
  WEBRTC,
  WEBRTC_TCP,
} from '../StreamPlayer/playerTechs';
import { getCurrentUser } from '~/modules/currentUser';

import { hasThumbnailContainer, insertThumbnailContainer } from './audioOnly';
import TheaterMode from '../TheaterMode';
import PictureInPictureButton from '../PictureInPictureButton';
import ViewCount from '../ViewCount';
import Portal from '../Portal';
import DownloadButton from '../DownloadButton';
import SYSTEM_TEXT from './constants';
import TimeDisplay from '../TimeDisplay';
import ContextMenu from '../ContextMenu';
import StreamStats from '../StreamStats';
import HelpBox from '../TechPicker/HelpBox';
import type { destroy, create, OvenPlayerInstance } from 'ovenplayer';

import './index.css';


declare global {
  interface Window {
    players?: Record<string, WeakRef<OvenPlayerInstance> | null>;
  }

  interface OvenPlayer {
    create: typeof create;
    destroy: typeof destroy;
  }

}

const LL_HLS_INDEX = 0;

const insertButton = (id, toward = 'right') => {
  const menu = document.querySelector(`#${id} .op-${toward}-controls`);
  const element = document.createElement('div');
  element.classList.add('op-navigators');
  element.classList.add('op-clear');
  if (toward === 'right') {
    menu?.insertBefore(element, menu.firstChild);
  } else {
    menu?.appendChild(element);
  }
  return element;
};

const seekToEndSafariHLS = (player, times = 0) => {
  const twoDays = 3600 * 1000 * 24 * 2;
  const isFinite = player.getDuration() === Infinity;
  if (isFinite) {
    player.seek(twoDays);
  } else if (times < 5) {
    setTimeout(() => {
      seekToEndSafariHLS(player, times + 1);
    }, 1000);
  }
};

const latencyControl = {
  liveSyncDuration: 5, // target latency
  liveMaxLatencyDuration: 15, // Max latency
  maxLiveSyncPlaybackRate: 1.1, // Catchup rate
};

const noLatencyControl = {
  liveSyncDuration: undefined,
  liveMaxLatencyDuration: undefined,
};

const OvenPlayerContainer = ({
  stream,
  debugSources,
  onError,
  vjsTech,
  setVjsTech,
  setDefault,
  autoplay,
  recordingId,
  startMuted,
  highBitrate,
  updatePlayerState,
  retries,
}) => {
  const location = useLocation();
  const disableLowLatency = location.search.includes('lowLatency=0');

  const currentUser = useSelector(getCurrentUser);
  const [pictureInPictureEnabled, setPictureInPictureEnabled] = useState(false);
  const [lowLatency, setLowLatency] = useState(disableLowLatency || true);
  const [readyTheaterMode, setReadyTheaterMode] = useState(false);
  const [readyPictureInPicture, setReadyPictureInPicture] = useState(false);
  const [readyViewCount, setReadyViewCount] = useState(false);
  const [readyDownload, setReadyDownload] = useState(false);
  const [ready, setReady] = useState(false);
  const [playStreamshotsWithoutAudio, setPlayStreamshotsWithoutAudio] = useState(false);
  const [playerStats, setPlayerStats] = useState({});
  const [streamStats, setStreamStats] = useState({});
  const [showStats, setShowStats] = useState(false);
  const [showWebRTCWarning, setShowWebRTCWarning] = useState(false);

  const container = useRef<HTMLElement>(null);
  const playerRef = useRef<OvenPlayerInstance | null>(null);
  const intl = useIntl();

  const initialVolume = useMemo(() => (typeof localStorage !== 'undefined' && 'volume' in localStorage ? (Number(localStorage.volume) * 100) : (100)), []);

  const sources = useMemo(() => {
    if (debugSources) return debugSources;

    let rawSources = getSourcesForOvenPlayer(stream, recordingId);

    if (highBitrate && !recordingId) {
      rawSources = rawSources.filter(source => source.name === AUDIO);
    }

    rawSources = rawSources.map((s) => {
      if (playStreamshotsWithoutAudio && s.label === STREAMSHOTS) {
        return { ...s, file: '/audio/1-minute-of-silence.mp3', type: 'mp3' };
      }
      return s;
    });
    const indexSource = recordingId ? rawSources.findIndex(source => source.name === 'RECORDING_MP4') : rawSources.findIndex(source => source.name === vjsTech);

    rawSources = rawSources.map((source, index) => {
      if (index === indexSource) {
        return {
          ...source,
          default: true,
        };
      }
      return source;
    });

    return rawSources;
  }, [stream.id, highBitrate, recordingId, playStreamshotsWithoutAudio]);

  useEffect(() => {
    localStorage.setItem('triedwebrtc', '1');
    setPictureInPictureEnabled(document.pictureInPictureEnabled);
  }, []);

  useEffect(() => {
    const currentContainer = container.current;
    const playerContainer = document.createElement('div');
    let vjsTechSelected = vjsTech;
    if (!currentContainer) return null;

    playerContainer.id = `op_stream_${stream.id}`;

    currentContainer.appendChild(playerContainer);

    const poster = stream.recordings.find(recording => recording.id === recordingId)?.thumb;

    const player: OvenPlayerInstance = OvenPlayer.create(playerContainer.id, {
      sources,
      loop: playStreamshotsWithoutAudio,
      disableSeekUI: playStreamshotsWithoutAudio,
      systemText: SYSTEM_TEXT,
      mute: startMuted || localStorage.muted === 'true',
      volume: Number.isFinite(initialVolume) ? initialVolume : 0,
      image: recordingId && poster ? poster : streamScreenshotURL(stream),
      autoStart: autoplay,
      autoFallback: false,
      showBigPlayButton: vjsTechSelected !== STREAMSHOTS,
      lowlatency: lowLatency && vjsTechSelected !== HLS,
      hlsConfig: {
        ...latencyControl,
        maxLiveSyncPlaybackRate: vjsTechSelected === HLS ? 1 : 1.5,
      },
    });


    let ourHls: unknown | null = null;
    // for debugging purposes
    if (!window.players) window.players = {}
    window.players[playerContainer.id] = new WeakRef(player);

    const bigPlayButton = document.createElement('div');
    bigPlayButton.className = 'op-bigbutton-container';
    bigPlayButton.innerHTML = '<i class="op-bigbutton op-con op-play-big"></i>';

    // Add a click handler that triggers the actual play:
    bigPlayButton.addEventListener('click', (e) => {
      e.stopPropagation(); 
      if (player) {
        player.play();
      }
    });

    const isSafari = /Safari/.test(navigator?.userAgent);

    let idleHlsTimeout: NodeJS.Timeout | null = null;
    // start 30s timeout, if the player hasn't played after 30s, stop loading hls
    if (vjsTechSelected === LL_HLS) {
      idleHlsTimeout = setTimeout(() => {
        if (player.getState() === 'paused' || player.getState() === 'idle') {
          if (ourHls) {
            console.warn('Stopping HLS load due to inactivity');
            // ourHls.stopLoad();
          }
        }
      }, 30000);
    }

    // Handler references for cleanup
    const handleStateChanged = ({ newstate: newState }) => {
      if (updatePlayerState) {
        updatePlayerState(newState);
      }

      const posterContainer = document.getElementById(`poster-${stream.id}`);
      const streamshotsOnSafari = (isSafari && (vjsTechSelected === STREAMSHOTS || vjsTechSelected === AUDIO));

      if (newState === 'paused' && document.hidden) {
        player.play();
      } else if (posterContainer && newState !== 'paused') {
        posterContainer.style.display = 'none';
      }
      if (posterContainer && streamshotsOnSafari) {
        posterContainer.style.display = 'flex';
      }

      const helpContainer = currentContainer?.querySelector('.op-helpers-container');
      const bigbutton = helpContainer?.querySelector('.op-bigbutton-container');
    
      if (newState === 'paused' && !bigbutton) {
        helpContainer?.appendChild(bigPlayButton);
      } else if (newState !== 'paused' && bigbutton) {
        bigbutton.remove();
      }

      if (newState === 'error') {
        if (vjsTechSelected !== STREAMSHOTS && vjsTechSelected !== AUDIO) {
          onError();
        } else {
          setPlayStreamshotsWithoutAudio(true);
        }
      }

      if (newState === 'paused') {
        // get hls
        if (ourHls && vjsTechSelected === LL_HLS) {
          console.log('Stopping HLS load due to pause');
          // ourHls.stopLoad();
        }
      } else if (newState === 'playing') {
        if (ourHls && vjsTechSelected === LL_HLS) {
          console.log('Resuming HLS load');
          // ourHls.startLoad();
        }
      }
    };

    const handleVolumeChanged = ({ volume }) => localStorage.setItem('volume', (Number.isFinite(volume / 100) ? (volume / 100) : 1));
    const handleMute = ({ mute }) => localStorage.setItem('muted', mute);
    const handleReady = (e) => {
      const buttonPictureInPicture = insertButton(playerContainer.id);
      const buttonTheaterMode = insertButton(playerContainer.id);
      const viewCount = insertButton(playerContainer.id, 'left');
      const downloadButton = insertButton(playerContainer.id);
      setReadyPictureInPicture(buttonPictureInPicture);
      setReadyTheaterMode(buttonTheaterMode);
      setReady(true);
      if (isSafari && vjsTechSelected === LL_HLS) {
        seekToEndSafariHLS(player);
      }

      if (!recordingId) {
        setReadyViewCount(viewCount);
      } else {
        setReadyDownload(downloadButton);
      }
      const indexSource = recordingId ? sources.findIndex(source => source.name === 'RECORDING_MP4') : sources.findIndex(source => source.name === vjsTech);
      player.setCurrentSource(indexSource);
    };

    const handleHlsPrepared = (hls) => {
      ourHls = hls;
    };
    const handleHlsDestroyed = () => {
      ourHls = null;
      console.log('HLS destroyed');
    };

    const handleSourceChanged = (data) => {
      console.log('Source changed!', data);
      let forceChange = false;
      
      if (!recordingId) {
        const name = sources.find((_, index) => index === data.currentSource)?.name;
        const posterContainer = document.getElementById(`poster-${stream.id}`);
        if (posterContainer) {
          posterContainer.remove();
        }
        if (name === LL_HLS && (vjsTechSelected === WEBRTC || vjsTechSelected === WEBRTC_TCP) && stream.has_b_frames) {
          vjsTechSelected = name;
          return;
        }

        if ((vjsTechSelected === WEBRTC || vjsTechSelected === WEBRTC_TCP) && stream.has_b_frames) {
          forceChange = true;
          player.setCurrentSource(LL_HLS_INDEX);
          setTimeout(() => {
            setShowWebRTCWarning(true);
            player.showControls(true);
          }, 1000);
          setTimeout(() => {
            setShowWebRTCWarning(false);
          }, 4000);
          return;
        }

        if (!forceChange) {
          console.log(`Setting default: ${name}`);
          setVjsTech(name);
          vjsTechSelected = name;
          if (!playStreamshotsWithoutAudio) {
            setDefault(OVENPLAYER, name);
          }
        }
      }
    };

    const handleClicked = () => {
      const webrtcItem = document.querySelector('[op-panel-type="source"][op-data-value="1"]');
      const webrtctcpItem = document.querySelector('[op-panel-type="source"][op-data-value="2"]');
      if (webrtcItem && webrtctcpItem && stream?.has_b_frames) {
        const preventClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
        };
        const title = intl.formatMessage({
          id: 'StreamerSettingsWarning',
          defaultMessage: "Streamer's settings are not compatible with WebRTC",
        });
        const warning = '<i class="ion-android-warning"></i>';
        const idIcon = '.op-setting-item-checked';
        webrtcItem.addEventListener('click', preventClick, true);  
        webrtcItem.style.opacity = '0.3';
        webrtcItem.setAttribute('title', title);
        const webrtcIcon = webrtcItem.querySelector(idIcon);
        webrtctcpItem.addEventListener('click', preventClick, true);
        webrtctcpItem.style.opacity = '0.3';
        webrtctcpItem.setAttribute('title', title);
        const webrtctcpIcon = webrtctcpItem.querySelector(idIcon);

        if (webrtcIcon && webrtctcpIcon) {
          webrtcIcon.innerHTML = warning;
          webrtcIcon.classList.add('op-show');
          webrtctcpIcon.innerHTML = warning;
          webrtctcpIcon.classList.add('op-show');
        }
      }
    };

    const handleSeek = () => {
      const duration = player.getDuration();
      const newPosition = player.getPosition();
      const tolerance = 15;

      const isLive = Math.abs(duration - newPosition) <= tolerance;

      const object = isLive ? latencyControl : noLatencyControl;

      Object.keys(object).forEach((key) => {
          if (ourHls) {
            ourHls.config[key] = object[key];
          } else {
            console.warn(`HLS not available, cannot set ${key} to ${object[key]}`);
          }
        });
    };

    player.on('stateChanged', handleStateChanged);
    player.on('volumeChanged', handleVolumeChanged);
    player.on('mute', handleMute);
    player.on('ready', handleReady);
    player.on('hlsPrepared', handleHlsPrepared);
    player.on('hlsDestroyed', handleHlsDestroyed);
    player.on('sourceChanged', handleSourceChanged);
    player.on('clicked', handleClicked);
    player.on('seek', handleSeek);

    currentContainer.addEventListener('contextmenu', e => e.stopPropagation(), { capture: true });

    playerRef.current = player;

    const updatePoster = async () => {
      if (player.getState() === 'playing' && vjsTechSelected !== AUDIO && vjsTechSelected !== STREAMSHOTS) return;
      
      if (document.visibilityState === 'visible' && !recordingId) {
        if (vjsTechSelected === STREAMSHOTS && !hasThumbnailContainer(currentContainer)) {
          insertThumbnailContainer(currentContainer);
        }

        const getUrl = () => loadStreamScreenshot(stream.id).catch(() => null);

        const img = currentContainer.querySelector('img');
        const url = await getUrl();

        if (img) {
          URL.revokeObjectURL(img.src);
          img.src = url;
        }
        const video = player?.getProvider() ? player?.getMediaElement() : null;

        if (video) {
          video.poster = url;
        } else {
          return;
        }

        const streamshotsOnSafari = (isSafari && (vjsTechSelected === STREAMSHOTS || vjsTechSelected === AUDIO));

        if (video?.readyState && video.parentNode.childElementCount === 1 && (vjsTechSelected !== STREAMSHOTS || isSafari)) {
          const posterContainer = document.createElement('div');
          posterContainer.id = `poster-${stream.id}`;
          posterContainer.style.display = streamshotsOnSafari ? 'flex' : 'none';
          posterContainer.style.justifyContent = 'center';
          posterContainer.style.alignItems = 'center';
          posterContainer.style.position = 'absolute';
          posterContainer.style.top = '0';
          posterContainer.style.left = '0';
          posterContainer.style.width = '100%';
          posterContainer.style.height = '100%';
          posterContainer.style.zIndex = '1';
          posterContainer.style.background = '#111';
          const posterTag = document.createElement('img');
          posterTag.style.width = '100%';
          posterTag.src = (url) || '';
          posterContainer.appendChild(posterTag);
          video.parentNode.appendChild(posterContainer);
        }
        if (((video?.readyState && video.paused && vjsTechSelected !== STREAMSHOTS) || streamshotsOnSafari) && video?.parentNode?.querySelector('img')) {
          video.parentNode.querySelector('img').parentNode.style.display = 'flex';
        }
      }
    };

    updatePoster();

    const interval = setInterval(updatePoster, 10000);
    const statsInterval = setInterval(async () => {
      const elementVideo = player.getMediaElement();
      setStreamStats({
        bitrate: Math.round((stream.bitrate || 0) / 1000),
        resolution: `${stream?.resolution}@${Math.round(stream?.framerate || 0)}`,
        viewport: `${elementVideo.clientWidth}x${elementVideo.clientHeight}`,
        codecs: `${stream?.video_codec}/${stream?.audio_codec}`,
      });
      if (!ourHls) return;

      const stats = {
        volume: player.getVolume(),
        playbackRate: player.getPlaybackRate(),
        latency: ourHls?.latency,
        isPlaying: player.getState() === 'playing',
        bandwidth: ourHls?.bandwidthEstimate,
        lowLatency,
      };
      setPlayerStats(stats);
    }, 15000);

    return () => {
      console.log('Removing player');

      if (window.players && window.players[playerContainer.id]) {
        window.players[playerContainer.id] = null;
      }

      bigPlayButton.remove();

      // Remove event listeners explicitly
      player.off('stateChanged', handleStateChanged);
      player.off('volumeChanged', handleVolumeChanged);
      player.off('mute', handleMute);
      player.off('ready', handleReady);
      player.off('hlsPrepared', handleHlsPrepared);
      player.off('hlsDestroyed', handleHlsDestroyed);
      player.off('sourceChanged', handleSourceChanged);
      player.off('clicked', handleClicked);
      player.off('seek', handleSeek);

      player.stop();
      player.off();
      player.remove();
      playerContainer.remove();

      setReadyPictureInPicture(false);
      setReadyTheaterMode(false);
      setReadyViewCount(false);
      setReadyDownload(false);

      clearInterval(interval);
      clearInterval(statsInterval);
      
      if (idleHlsTimeout) {
        clearTimeout(idleHlsTimeout);
      }
      
      idleHlsTimeout = null;
      ourHls = null;
      setReady(false);
    };
  }, [recordingId, autoplay, retries, playStreamshotsWithoutAudio]);

  const seekToLive = useCallback(() => {
    playerRef.current?.seek(playerRef.current?.getDuration());
  }, []);

  /**
   * Autoplay change handler
   */
  useEffect(() => {
    if (ready) {
      if (autoplay) {
        playerRef.current?.play();
      } else {
        playerRef.current?.pause();
      }
    }
  }, [autoplay, ready]);

  return (
    <>
      <div className={classNames('OvenPlayer', { 'recording': recordingId, 'streamshots_mute': (playStreamshotsWithoutAudio && (vjsTech === STREAMSHOTS || vjsTech === AUDIO)) })} ref={container} />
      {readyTheaterMode && <Portal parent={readyTheaterMode}><TheaterMode /></Portal>}
      {pictureInPictureEnabled && readyPictureInPicture && (
      <Portal parent={readyPictureInPicture}>
        <PictureInPictureButton onClick={() => playerRef.current?.getMediaElement().requestPictureInPicture()} />
      </Portal>
      )}
      {readyViewCount && <Portal parent={readyViewCount}><ViewCount stream={stream} /></Portal>}
      {readyDownload && <Portal parent={readyDownload}><DownloadButton link={sources[0].file} /></Portal>}
      {
        (playStreamshotsWithoutAudio && (vjsTech === STREAMSHOTS || vjsTech === AUDIO)) && (
          <Portal parent={document.querySelector(`#op_stream_${stream.id} .op-volume-button`)}>
            <i className="op-con op-volume-mute streamshots" title="Audio connection failed" />
          </Portal>
        )
      }
      {
        ready && recordingId === null && document.querySelector(`#op_stream_${stream.id} .op-time-display`) && (
          <Portal parent={document.querySelector(`#op_stream_${stream.id} .op-time-display`)}>
            <TimeDisplay onClick={seekToLive} startDate={stream.live_since} />
          </Portal>
        )
      }
      {ready && !!Object.keys(streamStats).length && (
        <Portal parent={document.body}>
          <ContextMenu container={container}>
            <div className="context-menu">
              <div
                className="context-menu-item"
                role="button"
                tabIndex={0}
                onClick={() => setShowStats(!showStats)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setShowStats(!showStats);
                  }
                }}
              >
                <div className="context-menu-item-icon">
                  <i className="ion-information-circled" />
                </div>
                <div className="context-menu-item-label">
                  <FormattedMessage id="ContextMenu_Stats" defaultMessage="Stats for nerds" />
                </div>
              </div>
              {
                stream?.username && stream?.username === currentUser?.username && (
                  <div
                    className="context-menu-item"
                    role="button"
                    tabIndex={0}
                    onClick={() => window.open('/optimizations', '_blank')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        window.open('/optimizations', '_blank');
                      }
                    }}
                  >
                    <div className="context-menu-item-icon">
                      <i className="ion-information-circled" />
                    </div>
                    <div className="context-menu-item-label">
                      <FormattedMessage id="ContextMenu_Stats" defaultMessage="Stream Analyzer" />
                    </div>
                  </div>
                )
              }
            </div>
          </ContextMenu>
        </Portal>
      )}
      {showStats && container.current && (
        <Portal parent={container.current.querySelector('.ovenplayer')}>
          <StreamStats {...streamStats} close={() => setShowStats(!showStats)} />
        </Portal>
      )}
      {ready && showWebRTCWarning && (
        <HelpBox parent={document.querySelector(`#op_stream_${stream.id} .op-setting-button`)}>
          <FormattedMessage id="WebRTCDisabled_Msg" defaultMessage="WebRTC disabled due to streamer's settings, falling back to HLS" />
        </HelpBox>
      )}
    </>
  );
};

export default OvenPlayerContainer;
